/**
 * EdgeCollapseTab — a vertically-centered drawer pull on a pane's inner edge.
 *
 * Chevron direction matches pane motion:
 *   - collapse → double chevron toward the pane wall
 *   - expand   → double chevron toward the canvas (emphasized styling)
 *
 * `side` is the pane's inner edge:
 *   - side="left"  → tab on the LEFT edge (Node Inspector)
 *   - side="right" → tab on the RIGHT edge (Sidebar)
 *
 * Parent MUST be `relative` and must NOT clip the protrusion with overflow-hidden.
 */
import React from 'react';
import { clsx } from 'clsx';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';

interface EdgeCollapseTabProps {
    side: 'left' | 'right';
    /** 'collapse' shrinks the pane, 'expand' opens it back out. */
    direction: 'collapse' | 'expand';
    onClick: () => void;
    label: string;
}

export const EdgeCollapseTab: React.FC<EdgeCollapseTabProps> = ({ side, direction, onClick, label }) => {
    const pointsRight =
        (side === 'left' && direction === 'collapse') ||
        (side === 'right' && direction === 'expand');
    const Chevron = pointsRight ? ChevronsRight : ChevronsLeft;
    const isExpand = direction === 'expand';

    const edgeClasses =
        side === 'left'
            ? 'left-0 -translate-x-1/2 rounded-l-lg border-r-0'
            : 'right-0 translate-x-1/2 rounded-r-lg border-l-0';

    return (
        <button
            type="button"
            onClick={onClick}
            title={label}
            aria-label={label}
            aria-expanded={isExpand}
            className={clsx(
                'absolute top-1/2 -translate-y-1/2 z-30 flex items-center justify-center',
                'transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                isExpand
                    ? 'w-7 h-14 bg-primary text-primary-foreground border border-primary hover:bg-primary/90'
                    : 'w-5 h-11 bg-card border border-border text-muted-foreground hover:bg-muted hover:text-foreground hover:border-foreground/30',
                edgeClasses,
            )}
        >
            <Chevron className={clsx(isExpand ? 'w-4 h-4' : 'w-3.5 h-3.5')} strokeWidth={isExpand ? 2.25 : 2} />
        </button>
    );
};
